import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BaseService } from '../../services/base.service';
import { WebSocketService } from '../../services/gateway.service';
import { NotificationService } from '../../services/notifications.service';
import { FiscalNotificationService } from '../../services/fiscal-notification.service';
import { ChatService } from '../../services/chat.service';
import { ThemeService } from '../../services/theme.service';
import { IError, IException } from '../../interfaces';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations, SENDER_TYPES } from '../../enum/constants';
import { interval, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../services/modal.service';
import { CHAT_TABS } from './chat-sidebar/components/chat-list/chat-list.interface';
import { StackedSidebarService } from 'src/app/services/stacked-sidebar.service';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  host: { class: 'fixed inset-0 !h-dvh min-h-0 overflow-hidden' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LayoutComponent implements OnInit {
  private readonly destroy$: Subject<null> = new Subject<null>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly notificationService: NotificationService,
    private readonly fiscalNotifications: FiscalNotificationService,
    private readonly chatService: ChatService,
    private readonly webSocketService: WebSocketService,
    protected readonly baseService: BaseService,
    protected readonly stackedSidebarService: StackedSidebarService,
    protected readonly themeService: ThemeService,
    private readonly translate: TranslateService,
    private readonly modalService: ModalService,
  ) {}

  @ViewChild('videoRef', { static: false }) videoRef!: ElementRef;

  @HostListener('window:resize')
  onResize() {
    this.baseService.isMobile = window.innerWidth <= 700;
  }

  @HostListener('window:orientationchange')
  onOrientation() {
    // if (screen.orientation.angle === 90) {
    //   this.notificationService.showInfo('Не роби цього!', 'Одному із наших розробників стало дуже боляче, поверни як було!');
    // }
  }

  ngOnInit() {
    this.themeService.getTheme();
    this.initSockets();

    this.cdr.detectChanges();
  }

  private initSockets() {
    this.webSocketService.reloadWindow().subscribe(() => {
      window.location.reload();
    });

    interval(15000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.webSocketService.ping();
      });

    this.webSocketService
      .notificationV2()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data[5]?.kind === 'fiscal_operation') {
            if (data[5].company_id !== String(this.baseService.company?.id)) return;
            // HTTP confirms acceptance; sockets report the outcome without a second queue toast.
            if (!['FISCAL_OPERATION_QUEUED', 'FISCAL_RECEIPT_DELIVERY_QUEUED'].includes(data[5].code)) {
              this.fiscalNotifications.show(data[5]);
            }
          } else if (data[4] === 'success') {
            this.notificationService.showSuccess(data[1], data[2]);
          } else if (data[4] === 'error') {
            this.notificationService.showError(data[1], data[2]);
          } else if (data[4] === 'warning') {
            this.notificationService.showWarning(data[1], data[2]);
          } else {
            this.notificationService.showInfo(data[1], data[2]);
          }
          this.notificationService.getCountNotification().pipe(takeUntil(this.destroy$)).subscribe();
        },
        error: () => {},
      });

    this.webSocketService.notification().subscribe({
      next: (data) => {
        this.chatService.loadCount();
        if (this.baseService.currentUserInfo.settings.is_notification) {
          this.notificationService.showInfo(
            `${activeTranslations.notifications_texts.info.new_messages} (${data})`,
            '',
          );
          if (this.baseService.currentUserInfo.settings.is_notification_sound) {
            this.notificationService.playSound();
          }
        }
      },
      error: (res) => {
        this.showSocketError(res);
      },
    });

    this.webSocketService.getNewMessage().subscribe({
      next: (data) => {
        const { chat, message, reactionAdded, error } = data;

        if (reactionAdded) {
          this.notifyAboutReaction(chat);
        } else {
          this.notifyAboutReceivedMessage(chat, message);
        }

        if (error) {
          this.parseAndShowError(error);
        }
      },
      error: (res) => {
        this.showSocketError(res);
      },
    });

    this.webSocketService.tariffPaymentCompleted().subscribe({
      next: (data) => {
        this.notificationService.showSuccess(
          '',
          activeTranslations.notifications_texts.success.successfully_paid_tariff,
        );
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      },
      error: (res) => {
        this.showSocketError(res);
      },
    });

    this.webSocketService.openPayment().subscribe({
      next: (data) => {
        this.modalService.openTariffUpdate();
      },
      error: (res) => {
        this.showSocketError(res);
      },
    });
  }

  private notifyAboutReceivedMessage(chat: any, message: any) {
    if (this.stackedSidebarService.chatsOpened) {
      this.chatService.getMessageAction(chat, message);
    }

    if (
      this.baseService.currentUserInfo.settings.is_notification &&
      chat.last_message?.sender_type === SENDER_TYPES.OPPONENT &&
      !this.stackedSidebarService.chatsOpened &&
      chat.tab_status !== CHAT_TABS.SPAM
    ) {
      let text = '';
      const attachmentType = message.attachments?.array?.[0]?.type;
      if (attachmentType) {
        text = this.translate.instant(`attachments.${attachmentType.toLowerCase()}`);
      } else {
        text = message.text;
      }

      this.notificationService.showInfoV2(chat.title, text).onTap.subscribe(() => {
        this.openChat(chat);
      });
      if (this.baseService.currentUserInfo.settings.is_notification_sound) {
        this.notificationService.playSound();
      }
    }
  }

  private notifyAboutReaction(chat: any) {
    if (
      this.baseService.currentUserInfo.settings.is_notification &&
      !this.stackedSidebarService.chatsOpened &&
      chat.tab_status !== CHAT_TABS.SPAM
    ) {
      this.notificationService.showInfoV2(chat.title, this.translate.instant('added_reaction')).onTap.subscribe(() => {
        this.openChat(chat);
      });

      if (this.baseService.currentUserInfo.settings.is_notification_sound) {
        this.notificationService.playSound();
      }
    }
  }

  private openChat(chat: any) {
    this.stackedSidebarService.openChats(chat.id).subscribe();
  }

  private showSocketError(res: any) {
    (res.error?.errors || []).forEach((err: IError) => {
      this.notificationService.showError(
        activeTranslations.notifications_texts.error.error,
        DEFAULT_ERROR,
      );
    });
  }

  videoPlay() {
    const media = this.videoRef.nativeElement;
    media.muted = true;
    media.play();
  }

  private parseAndShowError(error: IException) {
    this.notificationService.showError(
      activeTranslations.notifications_texts.error.error,
      ERROR_MESSAGES[error.code] || DEFAULT_ERROR,
    );
  }
}
